import {useEffect} from "react";
import MapElementProps from "./MapElementProps";
import {useAppSelector} from "../../redux/hooks";
import {MapState, selectMapState} from "../../redux/features/map/slice";
import {track} from "../../helpers/analytics";

// Remembers that the user denied the one-time geolocation prompt so we never
// re-request on subsequent visits.
const GEOLOCATION_DENIED_KEY = 'initial-geolocation-denied';

// Fallback view when geolocation is denied/unavailable: a populated world view
// centered on Europe instead of the null-island ocean at (0, 0).
const FALLBACK_CENTER: [number, number] = [10, 45];
const FALLBACK_ZOOM = 2.5;
const USER_AREA_ZOOM = 13;

// Mapbox `hash: true` URLs look like #zoom/lat/lng — a shared link already
// carries a viewport, so we must not override it.
const hasUrlHash = () => /^#\d/.test(window.location.hash);

// The map slice persists to localStorage; anything other than the (0, 0) zoom 2
// initial state means the user already has a viewport from a previous visit.
const isDefaultView = (mapState: MapState) =>
    Math.abs(mapState.locationAndZoom.location.longitude) < 1e-6 &&
    Math.abs(mapState.locationAndZoom.location.latitude) < 1e-6 &&
    Math.abs(mapState.locationAndZoom.zoom - 2) < 1e-6;

/**
 * On a true first visit (no URL hash, no persisted viewport) asks the browser
 * for the user's position ONCE and flies the map to their area. Read-only:
 * never writes to the location slice and never triggers painting.
 */
const InitialViewport = ({map}: MapElementProps) => {
    const mapState: MapState = useAppSelector(selectMapState);

    useEffect(() => {
        if (!map)
            return;
        if (hasUrlHash() || !isDefaultView(mapState))
            return;

        let cancelled = false;
        const flyTo = (center: [number, number], zoom: number) => {
            if (!cancelled)
                map.flyTo({center: center, zoom: zoom});
        };

        if (localStorage.getItem(GEOLOCATION_DENIED_KEY) === '1' || !navigator.geolocation) {
            flyTo(FALLBACK_CENTER, FALLBACK_ZOOM);
            return;
        }

        navigator.geolocation.getCurrentPosition(
            (position) => {
                track('initial_viewport_geolocated');
                flyTo([position.coords.longitude, position.coords.latitude], USER_AREA_ZOOM);
            },
            (error) => {
                if (error.code === error.PERMISSION_DENIED) {
                    localStorage.setItem(GEOLOCATION_DENIED_KEY, '1');
                }
                track('initial_viewport_fallback', { reason: error.code });
                flyTo(FALLBACK_CENTER, FALLBACK_ZOOM);
            },
            { timeout: 8000, maximumAge: 300000 }
        );

        return () => {
            cancelled = true;
        };
    }, [map]);

    return null;
};

export default InitialViewport;
